import { 
  collection, doc, getDocs, addDoc, updateDoc, deleteDoc, 
  query, where, writeBatch, onSnapshot
} from 'firebase/firestore';
import { examDb } from '../../config/firebase';
import { COLLECTIONS, NOTICE_STATUS, NOTIFICATION_TYPES, AUDIT_ACTIONS, NOTIFICATION_AUDIENCE } from '../../config/constants';
import auditService from '../audit/auditService';

class NotificationService {
  
  // ═══════════════════════════════════════════
  //  NOTICE BOARD (Global Notices)
  // ═══════════════════════════════════════════

  async createNotice(noticeData, user) {
    const payload = {
      ...noticeData,
      status: noticeData.status || NOTICE_STATUS.DRAFT,
      deliverySettings: noticeData.deliverySettings || { sendInApp: true, sendPush: false, sendEmail: false, sendSMS: false },
      createdBy: user.uid,
      createdByName: user.displayName,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(examDb, COLLECTIONS.NOTIFICATIONS), payload);
    payload.id = docRef.id;

    await auditService.log({
      action: AUDIT_ACTIONS.CREATE,
      collection: COLLECTIONS.NOTIFICATIONS,
      documentId: docRef.id,
      performedBy: user.uid,
      performedByName: user.displayName,
      performedByRole: user.role,
      description: `Created notice: ${payload.title}`,
    });

    return payload;
  }

  async updateNotice(id, noticeData, user) {
    const docRef = doc(examDb, COLLECTIONS.NOTIFICATIONS, id);
    const payload = { ...noticeData, updatedAt: new Date().toISOString() };
    await updateDoc(docRef, payload);
    await auditService.log({
      action: AUDIT_ACTIONS.UPDATE,
      collection: COLLECTIONS.NOTIFICATIONS,
      documentId: id,
      performedBy: user.uid,
      performedByName: user.displayName,
      performedByRole: user.role,
      description: `Updated notice: ${noticeData.title || id}`,
    });
  }

  async deleteNotice(id, user) {
    const docRef = doc(examDb, COLLECTIONS.NOTIFICATIONS, id);
    await deleteDoc(docRef);
    await auditService.log({
      action: AUDIT_ACTIONS.DELETE,
      collection: COLLECTIONS.NOTIFICATIONS,
      documentId: id,
      performedBy: user.uid,
      performedByName: user.displayName,
      performedByRole: user.role,
      description: `Deleted notice: ${id}`,
    });
  }

  async publishNotice(id, user) {
    const docRef = doc(examDb, COLLECTIONS.NOTIFICATIONS, id);
    await updateDoc(docRef, {
      status: NOTICE_STATUS.PUBLISHED,
      publishDate: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    await auditService.log({
      action: AUDIT_ACTIONS.PUBLISH,
      collection: COLLECTIONS.NOTIFICATIONS,
      documentId: id,
      performedBy: user.uid,
      performedByName: user.displayName,
      performedByRole: user.role,
      description: `Published notice: ${id}`,
    });
  }

  async archiveNotice(id, user) {
    const docRef = doc(examDb, COLLECTIONS.NOTIFICATIONS, id);
    await updateDoc(docRef, {
      status: NOTICE_STATUS.ARCHIVED,
      updatedAt: new Date().toISOString()
    });
    await auditService.log({
      action: AUDIT_ACTIONS.UPDATE,
      collection: COLLECTIONS.NOTIFICATIONS,
      documentId: id,
      performedBy: user.uid,
      performedByName: user.displayName,
      performedByRole: user.role,
      description: `Archived notice: ${id}`,
    });
  }

  async getNotices(filters = {}) {
    const q = query(collection(examDb, COLLECTIONS.NOTIFICATIONS));
    const snap = await getDocs(q);
    let notices = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Auto-archive expired notices on read
    const now = new Date();
    notices = notices.map(notice => {
      if (notice.status === NOTICE_STATUS.PUBLISHED && notice.expireDate && new Date(notice.expireDate) < now) {
        notice.status = NOTICE_STATUS.ARCHIVED;
        updateDoc(doc(examDb, COLLECTIONS.NOTIFICATIONS, notice.id), { status: NOTICE_STATUS.ARCHIVED });
      }
      return notice;
    });

    notices.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    if (filters.status) notices = notices.filter(n => n.status === filters.status);
    if (filters.priority) notices = notices.filter(n => n.priority === filters.priority);

    return notices;
  }

  // ═══════════════════════════════════════════
  //  USER NOTIFICATIONS (Bell & Alerts)
  // ═══════════════════════════════════════════

  async sendNotificationToUser(userId, notificationData) {
    const payload = {
      userId,
      ...notificationData,
      isRead: false,
      readAt: null,
      deliveryStatus: { inApp: true, push: false, email: false, sms: false },
      createdAt: new Date().toISOString()
    };
    await addDoc(collection(examDb, COLLECTIONS.USER_NOTIFICATIONS), payload);
  }

  async sendNotificationToRole(role, notificationData) {
    const payload = {
      userId: `ROLE_${role.toUpperCase()}`,
      ...notificationData,
      createdAt: new Date().toISOString()
    };
    await addDoc(collection(examDb, COLLECTIONS.USER_NOTIFICATIONS), payload);
  }

  /**
   * Subscribes to unread notifications and merges personal, role, and global sources in real-time.
   */
  subscribeToUnreadNotifications(userProfile, callback) {
    const roleId = `ROLE_${userProfile.role.toUpperCase()}`;
    
    let personalDocs = [];
    let roleDocs = [];
    let globalDocs = [];

    const notifyChange = () => {
      const allUnread = [];
      const readIds = new Set(personalDocs.filter(d => d.notificationId).map(d => d.notificationId));

      // 1. Unread Personal (No notificationId, isRead == false)
      personalDocs.filter(d => !d.notificationId && !d.isRead).forEach(d => allUnread.push(d));

      // 2. Unread Role-based
      roleDocs.filter(d => !readIds.has(d.id)).forEach(d => allUnread.push({ ...d, isRoleNotice: true }));

      // 3. Unread Global Notices
      const now = new Date();
      globalDocs.forEach(notice => {
        // Auto-archive memory representation if expired
        if (notice.expireDate && new Date(notice.expireDate) < now) return;

        const audienceMatch = 
          notice.targetAudience === NOTIFICATION_AUDIENCE.ALL ||
          (notice.targetAudience === NOTIFICATION_AUDIENCE.TEACHERS && userProfile.role === 'teacher') ||
          (notice.targetAudience === NOTIFICATION_AUDIENCE.ADMINS && (userProfile.role === 'admin' || userProfile.role === 'superAdmin'));
        
        if (audienceMatch && !readIds.has(notice.id)) {
          allUnread.push({ 
            id: notice.id, 
            type: NOTIFICATION_TYPES.NOTICE, 
            isGlobalNotice: true,
            ...notice 
          });
        }
      });

      allUnread.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      callback(allUnread);
    };

    const unsubPersonal = onSnapshot(
      query(collection(examDb, COLLECTIONS.USER_NOTIFICATIONS), where('userId', '==', userProfile.uid)),
      (snap) => {
        personalDocs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        notifyChange();
      }
    );

    const unsubRole = onSnapshot(
      query(collection(examDb, COLLECTIONS.USER_NOTIFICATIONS), where('userId', '==', roleId)),
      (snap) => {
        roleDocs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        notifyChange();
      }
    );

    const unsubGlobal = onSnapshot(
      query(collection(examDb, COLLECTIONS.NOTIFICATIONS), where('status', '==', NOTICE_STATUS.PUBLISHED)),
      (snap) => {
        globalDocs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        notifyChange();
      }
    );

    // Return combined unsubscribe
    return () => {
      unsubPersonal();
      unsubRole();
      unsubGlobal();
    };
  }

  /**
   * Subscribes to ALL notifications (both read and unread) and merges personal, role, and global sources in real-time.
   */
  subscribeToAllNotifications(userProfile, callback) {
    const roleId = `ROLE_${userProfile.role.toUpperCase()}`;
    
    let personalDocs = [];
    let roleDocs = [];
    let globalDocs = [];

    const notifyChange = () => {
      const allNotifications = [];
      const readReceipts = new Map(personalDocs.filter(d => d.notificationId).map(d => [d.notificationId, d]));

      // 1. Personal direct notifications
      personalDocs.filter(d => !d.notificationId).forEach(d => {
        allNotifications.push(d);
      });

      // 2. Role-based
      roleDocs.forEach(d => {
        const receipt = readReceipts.get(d.id);
        allNotifications.push({ 
          ...d, 
          isRoleNotice: true,
          isRead: !!receipt,
          readAt: receipt ? receipt.readAt : null
        });
      });

      // 3. Global Notices
      const now = new Date();
      globalDocs.forEach(notice => {
        // Auto-archive memory representation if expired
        if (notice.expireDate && new Date(notice.expireDate) < now) return;

        const audienceMatch = 
          notice.targetAudience === NOTIFICATION_AUDIENCE.ALL ||
          (notice.targetAudience === NOTIFICATION_AUDIENCE.TEACHERS && userProfile.role === 'teacher') ||
          (notice.targetAudience === NOTIFICATION_AUDIENCE.ADMINS && (userProfile.role === 'admin' || userProfile.role === 'superAdmin'));
        
        if (audienceMatch) {
          const receipt = readReceipts.get(notice.id);
          allNotifications.push({ 
            id: notice.id, 
            type: NOTIFICATION_TYPES.NOTICE, 
            isGlobalNotice: true,
            isRead: !!receipt,
            readAt: receipt ? receipt.readAt : null,
            ...notice 
          });
        }
      });

      allNotifications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      // Limit to 150 to keep UI responsive for history
      callback(allNotifications.slice(0, 150));
    };

    const unsubPersonal = onSnapshot(
      query(collection(examDb, COLLECTIONS.USER_NOTIFICATIONS), where('userId', '==', userProfile.uid)),
      (snap) => {
        personalDocs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        notifyChange();
      }
    );

    const unsubRole = onSnapshot(
      query(collection(examDb, COLLECTIONS.USER_NOTIFICATIONS), where('userId', '==', roleId)),
      (snap) => {
        roleDocs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        notifyChange();
      }
    );

    const unsubGlobal = onSnapshot(
      query(collection(examDb, COLLECTIONS.NOTIFICATIONS), where('status', '==', NOTICE_STATUS.PUBLISHED)),
      (snap) => {
        globalDocs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        notifyChange();
      }
    );

    return () => {
      unsubPersonal();
      unsubRole();
      unsubGlobal();
    };
  }

  async markAsRead(notification, userId) {
    if (notification.isGlobalNotice || notification.isRoleNotice) {
      // Create a receipt in userNotifications
      await addDoc(collection(examDb, COLLECTIONS.USER_NOTIFICATIONS), {
        userId,
        notificationId: notification.id,
        isRead: true,
        readAt: new Date().toISOString()
      });
    } else {
      // Update personal notification
      const docRef = doc(examDb, COLLECTIONS.USER_NOTIFICATIONS, notification.id);
      await updateDoc(docRef, { isRead: true, readAt: new Date().toISOString() });
    }
  }

  async markAllAsRead(userProfile, unreadList) {
    const batch = writeBatch(examDb);
    let count = 0;

    for (const notif of unreadList) {
      if (count >= 500) break; // Firestore batch limit

      if (notif.isGlobalNotice || notif.isRoleNotice) {
        const ref = doc(collection(examDb, COLLECTIONS.USER_NOTIFICATIONS));
        batch.set(ref, {
          userId: userProfile.uid,
          notificationId: notif.id,
          isRead: true,
          readAt: new Date().toISOString()
        });
        count++;
      } else {
        const ref = doc(examDb, COLLECTIONS.USER_NOTIFICATIONS, notif.id);
        batch.update(ref, { isRead: true, readAt: new Date().toISOString() });
        count++;
      }
    }

    if (count > 0) {
      await batch.commit();
    }
  }
}

export const notificationService = new NotificationService();
export default notificationService;
